import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  COMPETENCY_PERMS as P,
  useCompetencyAction,
  useCompetencyCan,
  useCompetencyOverview,
  useJobProfile,
  useTrainingCourses,
} from '@/hooks/useCompetency';
import { competencyService, type JobProfile, type JobProfileRequirement } from '@/services/competency.service';
import departmentService from '@/services/department.service';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { COMPETENCY_BASE, COMPETENCY_LEVELS, REQUIREMENT_TYPES, employeeLabel } from './competencyDisplay';

const { Text } = Typography;

const JobProfileDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const canManage = useCompetencyCan(P.jobProfileManage);
  const [form] = Form.useForm();
  const [toAssign, setToAssign] = useState<string[]>([]);
  const [toRemove, setToRemove] = useState<string[]>([]);

  const { data, isLoading } = useJobProfile(isNew ? undefined : id);
  const { data: courses = [] } = useTrainingCourses();
  const { data: employees = [] } = useCompetencyOverview(undefined, canManage && !isNew);
  const { data: departments = [] } = useQuery({ queryKey: ['departments', 'selection'], queryFn: () => departmentService.getForSelection(), staleTime: 300_000 });

  useEffect(() => {
    if (!data) return;
    form.setFieldsValue({
      ...data,
      is_active: !!Number(data.is_active),
      effective_date: data.effective_date ? dayjs(data.effective_date) : undefined,
      requirements: (data.requirements ?? []).map((r) => ({ ...r, is_mandatory: !!Number(r.is_mandatory) })),
    });
  }, [data, form]);

  const save = useCompetencyAction(
    (values: Partial<JobProfile>) => competencyService.saveJobProfile(isNew ? null : (id as string), values),
    'Job description saved'
  );
  const remove = useCompetencyAction(() => competencyService.deleteJobProfile(id as string), 'Job description deleted');
  const assign = useCompetencyAction((ids: string[]) => competencyService.assignJobProfile(id as string, ids), 'Employees assigned');
  const unassign = useCompetencyAction((ids: string[]) => competencyService.unassignJobProfile(ids), 'Employees removed');

  const assignedIds = useMemo(() => new Set((data?.employees ?? []).map((e) => e.id)), [data]);

  if (!isNew && isLoading) return <Spin style={{ margin: 48 }} />;
  if (!isNew && !data) return <Alert type="error" showIcon style={{ margin: 24 }} message="Job description not found" />;

  const onFinish = async (v: Record<string, any>) => {
    const payload = {
      ...v,
      effective_date: v.effective_date ? v.effective_date.format('YYYY-MM-DD') : null,
      requirements: (v.requirements ?? []) as JobProfileRequirement[],
    };
    const saved = await save.mutateAsync(payload);
    if (isNew && saved?.id) navigate(`${COMPETENCY_BASE}/job-profiles/${saved.id}`, { replace: true });
  };

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title={isNew ? 'New job description' : `${data?.code} - ${data?.title}`}
        extra={
          <Space wrap>
            {!isNew && (
              <ReportDocumentActions
                reportKey="competency-job-description"
                params={{ id: id as string }}
                documentId={id}
                sendPermissions={[P.jobProfileManage]}
              />
            )}
            {canManage && !isNew && (
              <Popconfirm
                title="Delete this job description?"
                description="Only possible when no employee is assigned."
                onConfirm={async () => {
                  await remove.mutateAsync();
                  navigate(`${COMPETENCY_BASE}/job-profiles`);
                }}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            )}
            {canManage && (
              <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} onClick={() => form.submit()}>
                Save
              </Button>
            )}
          </Space>
        }
      />
      <Form form={form} layout="vertical" disabled={!canManage} onFinish={onFinish} initialValues={{ is_active: true, requirements: [] }}>
        <Card title="Job description" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="code" label="Code" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item name="title" label="Job title" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="department_id" label="Department">
                <Select allowClear showSearch optionFilterProp="label" options={departments.map((d) => ({ value: d.id, label: d.department_name }))} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="purpose" label="Purpose of the job">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="responsibilities" label="Duties and responsibilities">
                <Input.TextArea rows={6} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="authorities" label="Authorities">
                <Input.TextArea rows={6} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="min_education" label="Minimum education">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="min_experience_years" label="Experience (years)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={5}>
              <Form.Item name="effective_date" label="Effective from">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="is_active" label=" " valuePropName="checked">
                <Checkbox>Active</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {!isNew && (
            <Text type="secondary">
              Revision {data?.revision_no ?? 0}. Saving the job description raises the revision.
            </Text>
          )}
        </Card>

        <Card title="Competence requirements" style={{ marginBottom: 16 }}>
          <Form.List name="requirements">
            {(fields, { add, remove: removeRow }) => (
              <>
                {fields.map((field) => (
                  <Form.Item noStyle key={field.key} shouldUpdate>
                    {() => {
                      const type = form.getFieldValue(['requirements', field.name, 'requirement_type']);
                      return (
                        <Row gutter={8} align="top" style={{ borderBottom: '1px solid #f0f0f0', paddingTop: 8 }}>
                          <Col xs={24} md={4}>
                            <Form.Item name={[field.name, 'requirement_type']} rules={[{ required: true, message: 'Type' }]}>
                              <Select placeholder="Type" options={REQUIREMENT_TYPES.map((t) => ({ value: t, label: t }))} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item name={[field.name, 'name']} rules={[{ required: true, message: 'Requirement' }]}>
                              <Input placeholder="Requirement" />
                            </Form.Item>
                          </Col>
                          {type === 'Competency' && (
                            <>
                              <Col xs={12} md={3}>
                                <Form.Item name={[field.name, 'competency_code']} rules={[{ required: true, message: 'Code' }]}>
                                  <Input placeholder="Competency code" />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={3}>
                                <Form.Item name={[field.name, 'required_level']}>
                                  <Select placeholder="Level" allowClear options={COMPETENCY_LEVELS.map((l) => ({ value: l, label: l }))} />
                                </Form.Item>
                              </Col>
                            </>
                          )}
                          {type === 'Certification' && (
                            <Col xs={24} md={6}>
                              <Form.Item name={[field.name, 'certification_type']}>
                                <Input placeholder="Certificate type (e.g. CSWIP 3.1)" />
                              </Form.Item>
                            </Col>
                          )}
                          {type === 'Training' && (
                            <Col xs={24} md={6}>
                              <Form.Item name={[field.name, 'training_course_id']} rules={[{ required: true, message: 'Course' }]}>
                                <Select
                                  placeholder="Training course"
                                  showSearch
                                  optionFilterProp="label"
                                  options={courses.map((c) => ({ value: c.id, label: `${c.code} - ${c.title}` }))}
                                />
                              </Form.Item>
                            </Col>
                          )}
                          {(type === 'Training' || type === 'Certification') && (
                            <Col xs={12} md={2}>
                              <Form.Item name={[field.name, 'refresher_months']}>
                                <InputNumber min={1} placeholder="Refresh (months)" style={{ width: '100%' }} />
                              </Form.Item>
                            </Col>
                          )}
                          <Col xs={12} md={2}>
                            <Form.Item name={[field.name, 'is_mandatory']} valuePropName="checked" initialValue>
                              <Checkbox>Mandatory</Checkbox>
                            </Form.Item>
                          </Col>
                          <Col flex="auto">
                            <Form.Item name={[field.name, 'notes']}>
                              <Input placeholder="Notes" />
                            </Form.Item>
                          </Col>
                          {canManage && (
                            <Col>
                              <Button danger icon={<DeleteOutlined />} onClick={() => removeRow(field.name)} />
                            </Col>
                          )}
                        </Row>
                      );
                    }}
                  </Form.Item>
                ))}
                {canManage && (
                  <Button icon={<PlusOutlined />} style={{ marginTop: 12 }} onClick={() => add({ requirement_type: 'Training', is_mandatory: true })}>
                    Add requirement
                  </Button>
                )}
              </>
            )}
          </Form.List>
        </Card>
      </Form>

      {!isNew && (
        <Card title={`Employees in this role (${data?.employees?.length ?? 0})`}>
          {canManage && (
            <Space wrap style={{ marginBottom: 12 }}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Add employees"
                style={{ minWidth: 420 }}
                value={toAssign}
                onChange={setToAssign}
                optionFilterProp="label"
                maxTagCount="responsive"
                options={employees
                  .filter((e) => !assignedIds.has(e.id))
                  .map((e) => ({
                    value: e.id,
                    label: `${employeeLabel(e)}${e.job_profile_code ? ` (now ${e.job_profile_code})` : ''}`,
                  }))}
              />
              <Button
                type="primary"
                disabled={!toAssign.length}
                loading={assign.isPending}
                onClick={async () => {
                  await assign.mutateAsync(toAssign);
                  setToAssign([]);
                }}
              >
                Assign
              </Button>
              <Popconfirm title={`Remove ${toRemove.length} employee(s) from this job description?`} onConfirm={async () => {
                await unassign.mutateAsync(toRemove);
                setToRemove([]);
              }}>
                <Button danger disabled={!toRemove.length}>
                  Remove selected
                </Button>
              </Popconfirm>
            </Space>
          )}
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={data?.employees ?? []}
            rowSelection={canManage ? { selectedRowKeys: toRemove, onChange: (k) => setToRemove(k as string[]) } : undefined}
            columns={[
              { title: 'Emp. No.', dataIndex: 'employee_number', width: 120 },
              { title: 'Name', dataIndex: 'full_name', render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/employees/${r.id}`)}>{v}</a> },
              { title: 'Department', dataIndex: 'department_name' },
              { title: 'Assigned', dataIndex: 'assigned_at', render: (v) => formatDate(v) || '-' },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default JobProfileDetailPage;
